import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiGet, ApiError } from '../api/client';
import type { AccountResponse, AccountUser } from '../api/types';

/**
 * AccountContext — fetches `/account` once for the authenticated shell so
 * the nav rail avatar/name and the Account/Plans/Settings views share a
 * single source of truth instead of each re-fetching.
 *
 * A 401 is not skipped here, so the API client's session-expired redirect
 * to /auth fires when the cookie is missing/expired — this is what guards
 * the /app/* shell.
 */
interface AccountContextValue {
  user: AccountUser | null;
  usage: Record<string, unknown> | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const AccountContext = createContext<AccountContextValue | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AccountResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<AccountResponse>('/account');
      setData(res);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Failed to load account.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AccountContextValue>(
    () => ({
      user: data?.user ?? null,
      usage: data?.usage ?? null,
      loading,
      error,
      refresh,
    }),
    [data, loading, error, refresh],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error('useAccount must be used within <AccountProvider>');
  return ctx;
}

/** Initials fallback for the nav-rail avatar when no picture is present. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
